import { Helmet } from "react-helmet-async";

interface PageHeadProps {
  title: string;
  description: string;
  path: string;
  jsonLd?: Record<string, unknown>;
  keywords?: string;
}

const SITE_NAME = "Batts Legal LLP";
const BASE_URL = "https://www.battslegal.com";

const defaultKeywords = "Alicia Batts, Alicia J. Batts, antitrust lawyer, antitrust attorney, competition law, merger review, FTC, DOJ, antitrust litigation, Washington DC antitrust, Batts Legal";

const defaultJsonLd = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: SITE_NAME,
  url: BASE_URL,
  description: "Alicia J. Batts and Batts Legal LLP provide strategic antitrust counsel for complex corporate matters including mergers, government investigations, and competition litigation.",
  founder: {
    "@type": "Person",
    name: "Alicia J. Batts",
    jobTitle: "Founding Partner",
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Columbia Law School" },
      { "@type": "CollegeOrUniversity", name: "Harvard College" },
    ],
    knowsAbout: ["Antitrust Law", "Competition Policy", "Merger Review", "FTC Investigations", "Antitrust Litigation"],
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Washington",
    addressRegion: "DC",
    addressCountry: "US",
  },
  telephone: "(202) 596-6160",
  email: "info@battslegal.com",
  areaServed: "US",
  priceRange: "$$$",
  knowsAbout: [
    "Antitrust Law",
    "Competition Policy",
    "Merger Review",
    "Government Investigations",
    "Antitrust Litigation",
    "FTC Enforcement",
    "DOJ Antitrust",
  ],
};

const PageHead = ({ title, description, path, jsonLd, keywords }: PageHeadProps) => {
  const fullTitle = path === "/" ? `Batts Legal LLP — Strategic Antitrust Counsel` : `${title} | Batts Legal`;
  const canonicalUrl = `${BASE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd ?? defaultJsonLd)}
      </script>
    </Helmet>
  );
};

export default PageHead;
